import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ContentItem } from '#pplx/frontend/aether/components/ContentItem/index.js';
import { BoxPrimitive } from '#pplx/frontend/aether/primitives/BoxPrimitive/index.js';
import { TextPrimitive } from '#pplx/frontend/aether/primitives/TextPrimitive/index.js';
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { getMentionSectionTitle } from '#pplx/frontend/components/form/lexical/plugins/mentionSectionTitle.js';
import { MENTION_PANEL_VIEWPORT_PADDING_PX, shouldFitMentionMenuToContent, checkForAtSignMentions, } from '#pplx/frontend/components/form/lexical/plugins/MentionsPluginShared.js';
import { filterMentionTypeaheadOptions } from '#pplx/frontend/components/form/lexical/plugins/MentionsPluginSupport.js';
import { prepareMentionSelection } from '#pplx/frontend/components/form/lexical/plugins/prepareMentionSelection.js';
import { CommandPanel, INLINE_PANEL_HEIGHT_PX, INLINE_PANEL_WIDTH_PX, } from '#pplx/frontend/composer-sdk/components/CommandPanel.js';
import { MentionNodeTextContent } from '#pplx/frontend/composer-sdk/lexical/nodes/MentionNode.js';
import { TOOLTIP_MAX_NUM_CHARACTERS } from '#pplx/frontend/composer-sdk/lexical/nodes/MentionNodeShared.js';
import { $createMentionNode } from '#pplx/frontend/composer-sdk/lexical/nodes/MentionNodeSupport.js';
import { truncateText } from '#pplx/frontend/composer-sdk/lexical/utils/truncateText.js';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { LexicalTypeaheadMenuPlugin, MenuOption } from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { $createTextNode } from 'lexical';
import { noop } from 'lodash-es';
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import * as ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { RemoveScroll } from 'react-remove-scroll';
// These are the recommended regular expressions from the Lexical Playground Mentions Plugin
// https://github.com/facebook/lexical/blob/main/packages/lexical-playground/src/plugins/MentionsPlugin/index.tsx
// Chars we expect to see in a mention (non-space, non-punctuation).
// Non-standard series of chars. Each series must be preceded and followed by
// a valid char.
// Regex used to match alias.
class MentionTypeaheadOption extends MenuOption {
    uuid;
    variant;
    label;
    subtitle;
    url;
    icon;
    queryText;
    localizedName;
    action;
    convertToNode;
    isAlwaysVisible;
    disabled;
    separatorBefore;
    rightElement;
    className;
    tooltipOverride;
    src;
    emoji;
    constructor(key, uuid, variant, label, subtitle, // optional subtitle text displayed below the label
    url, // @tab displays a label but the url is the actual query text
    icon, // visual representation (emoji, tabler icon, or image url)
    queryText, // @shortcuts displays a label but prompt becomes the query text
    localizedName, // pre-computed locale-aware display label; `label` stays as the canonical slug-shaped value
    action, // the method that is called when the option is selected
    convertToNode, // whether the option should be converted to a node after its action is run
    isAlwaysVisible, // whether the option should appear in the menu even if it does not meet the filter condition
    disabled, // whether the option is disabled and cannot be selected
    separatorBefore, // whether a divider should be shown before the option
    rightElement, // the element that is displayed on the right side of the option
    className, // the class name of the option
    tooltipOverride, // overrides queryText as tooltip content for this row
    // Deprecated: use icon instead
    src, emoji) {
        super(key);
        this.uuid = uuid;
        this.variant = variant;
        this.label = label;
        this.subtitle = subtitle;
        this.url = url;
        this.icon = icon;
        this.queryText = queryText;
        this.localizedName = localizedName;
        this.action = action;
        this.convertToNode = convertToNode;
        this.isAlwaysVisible = isAlwaysVisible;
        this.disabled = disabled;
        this.separatorBefore = separatorBefore;
        this.rightElement = rightElement;
        this.className = className;
        this.tooltipOverride = tooltipOverride;
        this.src = src;
        this.emoji = emoji;
    }
}
const MentionMenuOptionRow = React.memo(React.forwardRef(function MentionMenuOptionRow({ option, focused, index, selectOptionAndCleanUp, setHighlightedIndex, }, ref) {
    const handleMouseMove = useCallback(() => {
        if (!option.disabled) {
            setHighlightedIndex(index);
        }
    }, [index, option.disabled, setHighlightedIndex]);
    const handleMouseDown = useCallback((e) => {
        if (!option.disabled) {
            // Prevent default mousedown behavior (e.g., text selection or focus shift)
            e.preventDefault();
            setHighlightedIndex(index);
            selectOptionAndCleanUp(option);
        }
    }, [index, option, selectOptionAndCleanUp, setHighlightedIndex]);
    return (_jsx("div", { ref: ref, role: "menuitem", "aria-disabled": option.disabled, className: [
            option.className,
            'w-full',
            focused && 'rounded-lg bg-subtle',
            option.disabled ? 'cursor-not-allowed' : 'cursor-pointer',
        ]
            .filter(Boolean)
            .join(' '), onMouseMove: handleMouseMove, onMouseDown: handleMouseDown, children: _jsx(ContentItem, { rounded: true, disabled: option.disabled, trailingAccessory: option.rightElement?.({ focused }), children: _jsxs("div", { className: "flex min-w-0 flex-col", children: [_jsx(MentionNodeTextContent, { className: "flex w-full items-center gap-2", text: option.label, localizedName: option.localizedName, mentionVariant: option.variant, icon: option.icon, truncate: option.rightElement ? 'small' : 'medium', variant: "small", textClassName: "leading-5", iconClassName: "!text-primary h-4 w-4", emojiClassName: "align-middle shrink-0", inlineAlignment: "middle", tooltipText: truncateText(option.tooltipOverride ?? option.queryText ?? '', TOOLTIP_MAX_NUM_CHARACTERS) }), option.subtitle && (_jsx(TextPrimitive, { className: option.icon ? 'ml-6' : undefined, color: "light", variant: "tinyRegular", truncate: true, children: option.subtitle }))] }) }) }));
}));
const MentionsPlugin = React.memo(function MentionsPlugin({ options, onOpen = noop, onClose = noop, onTrigger, onSearchQueryChange = noop, spacesCopyVariant = 'space', ref, }) {
    const { $t } = useIntl();
    const [editor] = useLexicalComposerContext();
    const [menuOpen, setMenuOpen] = useState(false);
    const [queryString, setQueryString] = useState(null);
    const [isFocused, setIsFocused] = useState(false);
    const handleFocus = useCallback(() => setIsFocused(true), []);
    const handleBlur = useCallback(() => setIsFocused(false), []);
    const handleOpen = useCallback(() => {
        setMenuOpen(true);
        onOpen();
    }, [onOpen]);
    const handleClose = useCallback(() => {
        setMenuOpen(false);
        onClose();
    }, [onClose]);
    const getSectionTitle = useCallback((variant) => getMentionSectionTitle({ $t, spacesCopyVariant, variant }), [$t, spacesCopyVariant]);
    useEffect(() => {
        return editor.registerRootListener((rootEl, prevEl) => {
            if (rootEl) {
                setIsFocused(rootEl === document.activeElement);
                rootEl.addEventListener('focus', handleFocus);
                rootEl.addEventListener('blur', handleBlur);
            }
            if (prevEl) {
                prevEl.removeEventListener('focus', handleFocus);
                prevEl.removeEventListener('blur', handleBlur);
            }
        });
    }, [editor, handleBlur, handleFocus]);
    const filteredOptions = useMemo(() => {
        return filterMentionTypeaheadOptions({ options, queryString }).map((option) => new MentionTypeaheadOption(option.uuid, option.uuid, option.variant, option.label, option.subtitle, option.url, option.icon, option.queryText, option.localizedName, option.action, option.convertToNode, option.isAlwaysVisible, option.disabled, option.separatorBefore, option.rightElement, option.className, option.tooltipOverride, option.src, option.emoji));
    }, [queryString, options]);
    const hasConvertibleOptions = useMemo(() => filteredOptions.some((option) => {
        const isConvertable = option.convertToNode === undefined ||
            option.convertToNode ||
            option.variant === 'search-mode';
        return !option.disabled && isConvertable;
    }), [filteredOptions]);
    const onQueryChange = useCallback((matchingString) => {
        setQueryString(matchingString);
        onSearchQueryChange(matchingString);
    }, [onSearchQueryChange]);
    const getPossibleQueryMatch = useCallback((text) => {
        const match = checkForAtSignMentions(text, 0);
        const matchedTrigger = match?.replaceableString?.[0];
        if (!matchedTrigger)
            return match;
        if (onTrigger?.(matchedTrigger, match.matchingString) === false)
            return null;
        return match;
    }, [onTrigger]);
    const scrollFocusedOptionIntoView = useCallback((node) => {
        node?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }, []);
    const getOptionNodes = useCallback((options, selectedIndex, selectOptionAndCleanUp, setHighlightedIndex) => {
        let currentVariant = '';
        const items = [];
        options.forEach((option, index) => {
            if (option.variant !== currentVariant) {
                currentVariant = option.variant;
                const sectionTitle = getSectionTitle(currentVariant);
                items.push(_jsxs(React.Fragment, { children: [items.length > 0 && _jsx(BoxPrimitive, { className: "border-soft my-1 mx-2 border-t" }), _jsx(BoxPrimitive, { className: "px-2 py-1", children: _jsx(TextPrimitive
                            // Prevent wrapping for non-english languages
                            , { 
                                // Prevent wrapping for non-english languages
                                className: "w-max", color: "light", variant: "tinyRegular", children: sectionTitle }) })] }, `${option.variant}-section`));
            }
            if (option.separatorBefore && options[index - 1]?.variant === option.variant) {
                items.push(_jsx(BoxPrimitive, { className: "border-soft my-1 mx-2 border-t" }, `${option.key}-separator`));
            }
            const focused = index === selectedIndex;
            items.push(_jsx(MentionMenuOptionRow, { ref: focused ? scrollFocusedOptionIntoView : undefined, option: option, focused: focused, index: index, selectOptionAndCleanUp: selectOptionAndCleanUp, setHighlightedIndex: setHighlightedIndex }, option.key));
        });
        return items;
    }, [getSectionTitle, scrollFocusedOptionIntoView]);
    const onSelectOption = useCallback((selectedOption, nodeToReplace, closeMenu) => {
        const selection = prepareMentionSelection({ option: selectedOption });
        switch (selection) {
            case 'disabled':
                return;
            case 'remove-trigger':
                editor.update(() => {
                    if (nodeToReplace) {
                        nodeToReplace.remove();
                    }
                });
                closeMenu();
                return;
            case 'insert-mention':
                break;
            default:
                return assertUnreachableValue(selection);
        }
        editor.update(() => {
            const mentionNode = $createMentionNode({
                uuid: selectedOption.uuid,
                variant: selectedOption.variant,
                text: selectedOption.label,
                url: selectedOption.url,
                icon: selectedOption.icon,
                queryText: selectedOption.queryText,
                localizedName: selectedOption.localizedName,
            });
            if (nodeToReplace) {
                nodeToReplace.replace(mentionNode);
            }
            const spaceNode = $createTextNode(' ');
            mentionNode.insertAfter(spaceNode);
            spaceNode.selectEnd();
            closeMenu();
        });
    }, [editor]);
    const menuRenderFn = useCallback((anchorElementRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) => {
        const calculatePosition = () => {
            if (!anchorElementRef.current) {
                return { isTop: false, viewportCollisionLeftOffsetPx: 0 };
            }
            const anchorRect = anchorElementRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - anchorRect.bottom;
            const isTop = spaceBelow < INLINE_PANEL_HEIGHT_PX && anchorRect.top > INLINE_PANEL_HEIGHT_PX;
            // Calculate horizontal position to keep popup within viewport
            let viewportCollisionLeftOffsetPx = 0;
            const rightEdge = anchorRect.left + INLINE_PANEL_WIDTH_PX;
            if (rightEdge > window.innerWidth) {
                // If popup would go beyond right edge, shift it left
                viewportCollisionLeftOffsetPx =
                    window.innerWidth - rightEdge - MENTION_PANEL_VIEWPORT_PADDING_PX;
            }
            // Ensure popup doesn't go beyond left edge
            const finalLeft = anchorRect.left + viewportCollisionLeftOffsetPx;
            if (finalLeft < MENTION_PANEL_VIEWPORT_PADDING_PX) {
                viewportCollisionLeftOffsetPx = MENTION_PANEL_VIEWPORT_PADDING_PX - anchorRect.left;
            }
            return { isTop, viewportCollisionLeftOffsetPx };
        };
        const { isTop, viewportCollisionLeftOffsetPx } = calculatePosition();
        const fitContent = shouldFitMentionMenuToContent(filteredOptions);
        if (!anchorElementRef.current || !filteredOptions.length) {
            return null;
        }
        return ReactDOM.createPortal(_jsx(RemoveScroll, { removeScrollBar: false, enabled: true, children: _jsx(CommandPanel, { variant: "inline", isTop: isTop, viewportCollisionLeftOffsetPx: viewportCollisionLeftOffsetPx, fitContent: fitContent, children: _jsx("div", { role: "menu", className: "flex w-full flex-col gap-px", children: getOptionNodes(filteredOptions, hasConvertibleOptions ? (selectedIndex ?? -1) : -1, selectOptionAndCleanUp, setHighlightedIndex) }) }) }), anchorElementRef.current);
    }, [filteredOptions, getOptionNodes, hasConvertibleOptions]);
    useImperativeHandle(ref, () => ({
        // Submit is allowed when the menu is closed OR when every option
        // in the menu cannot be converted into a mention node. For example,
        // the user can submit when the only option is "create a shortcut"
        canSubmit: () => !menuOpen || !hasConvertibleOptions,
    }), [menuOpen, hasConvertibleOptions]);
    return isFocused ? (_jsx(LexicalTypeaheadMenuPlugin, { onQueryChange: onQueryChange, onSelectOption: onSelectOption, triggerFn: getPossibleQueryMatch, preselectFirstItem: hasConvertibleOptions, onOpen: handleOpen, onClose: handleClose, options: filteredOptions, menuRenderFn: menuRenderFn })) : null;
});
export default MentionsPlugin;
//# sourceMappingURL=MentionsPlugin.js.map